import axiosClient from "./axiosClient";
import getMeCache from "./get-me-cache";

const authApi = {
    signUpStep1: (data) => {
        const baseURL = "user/signup/step1";
        return axiosClient.post(baseURL, {
            email: data.email,
            ref: data.ref,
        });
    },
    signUpStep2: (data) => {
        const baseURL = "user/signup/step2";
        return axiosClient.post(baseURL, {
            email: data.email,
            OTPCode: data.OTPCode,
        });
    },
    signUpStep3: (data) => {
        const baseURL = "user/signup/step3";
        return axiosClient.post(baseURL, {
            email: data.email,
            password: data.password,
            oneTimeToken: data.oneTimeToken,
        });
    },
    login: (data) => {
        const baseURL = "user/login";
        return axiosClient.post(baseURL, {
            email: data.email,
            password: data.password,
        });
    },
    socialLoginGoogle: (idToken) => {
        return axiosClient.post("user/auth/google", { idToken });
    },
    /**
     * Hồ sơ user + số dư + notifications. `getMe` ở backend tốn 6 truy vấn nên đi
     * qua getMeCache: các lời gọi trùng trong ~3s chỉ tốn 1 lượt ra server.
     * @param {boolean} [force] ép ra server (thường không cần: mọi request ghi dữ
     *                          liệu đã tự reset cache ở axiosClient).
     */
    getUserInfo(force = false) {
        const baseURL = "user/get-me";
        return getMeCache.run(() => axiosClient.get(baseURL), force);
    },
    updateMe(data) {
        const baseURL = "user/update-me";
        return axiosClient.post(baseURL, data);
    },
    applyReferral(ref) {
        const baseURL = "user/apply-referral";
        return axiosClient.post(baseURL, { ref });
    },
    // Deferred deep link: ghi {ip, ref} khi user vào trang /tai-app → app khớp lại theo IP sau khi cài.
    trackReferralVisit(ref) {
        return axiosClient.post("user/referral/track-visit", { ref });
    },
    getOTP(purpose) {
        const baseURL = "user/otp";
        // purpose: tách cooldown/OTP theo mục đích. Chỉ nhận string; arg cũ ({email}) → bỏ qua, mặc định "account".
        const params = typeof purpose === "string" && purpose ? { purpose } : {};
        return axiosClient.get(baseURL, { params });
    },
    verifyOTP(data) {
        const baseURL = "user/otp";
        return axiosClient.post(baseURL, {
            OTPCode: data.OTPCode,
        });
    },
    // Xác minh email cho user đã đăng nhập nhưng chưa verified.
    // Gửi OTP trước qua getOTP("verify_email"), rồi gọi hàm này để đánh dấu verified.
    verifyEmail(data) {
        const baseURL = "user/verify-email";
        return axiosClient.post(baseURL, {
            OTPCode: data.OTPCode,
        });
    },
    setOtpNewEmail(data) {
        const baseURL = "user/send-otp-new-email";
        return axiosClient.post(baseURL, {
            oneTimeToken: data.oneTimeToken,
            newEmail: data.newEmail,
        });
    },
    changeEmail: (data) => {
        const baseURL = "user/change-email";
        return axiosClient.post(baseURL, {
            newEmail: data.newEmail,
            OTPCode: data.OTPCode,
            isMiddleware: true,
        });
    },
    changePassword: (data) => {
        const baseURL = "user/change-password";
        return axiosClient.post(baseURL, {
            newPassword: data.newPassword,
            oneTimeToken: data.oneTimeToken,
        });
    },
    changeBankInfo: (data) => {
        const baseURL = "user/change-bank-info";
        return axiosClient.post(baseURL, {
            bankInfo: data.bankInfo,
            oneTimeToken: data.oneTimeToken,
        });
    },
    saveBankInfoFirstTime: ({ bankId, bankAccountName, bankAccountNumber }) => {
        const baseURL = "user/save_bank_first_time";
        return axiosClient.post(baseURL, {
            bankAccountName,
            bankAccountNumber,
            bankId,
        });
    },
    forgotPassword: (data) => {
        const baseURL = "user/forgot-password";
        return axiosClient.post(baseURL, {
            email: data.email,
        });
    },
    verifyForgotPassword: (data) => {
        const baseURL = "user/verify-reset-password";
        return axiosClient.post(baseURL, {
            email: data.email,
            OTPCode: data.OTPCode,
        });
    },
    resetPassword: (data) => {
        const baseURL = "user/reset-password";
        return axiosClient.post(baseURL, {
            newPassword: data.newPassword,
            oneTimeToken: data.oneTimeToken,
        });
    },
    myOrders: (queryString) => {
        const baseURL = `user/my-orders?&sort=-purchaseTime&${queryString || ""}`;
        return axiosClient.get(baseURL);
    },
    myRefs: () => {
        const baseURL = `user/my-refs`;
        return axiosClient.get(baseURL);
    },
    myPendingCash: () => {
        const baseURL = `user/my-pending-cash`;
        return axiosClient.get(baseURL);
    },
    // Lịch sử biến động số dư (hoàn tiền, thưởng, rút tiền, đổi voucher...)
    cashHistory: ({ limit = 30, offset = 0, category = 'all' } = {}) => {
        // Chỉ gửi `category` khi thực sự lọc — BE coi thiếu param là "all".
        const params = { limit, offset };
        if (category && category !== 'all') params.category = category;
        return axiosClient.get(`user/cash-history`, { params });
    },
    /**
     * Danh sách thông báo của mình (1 truy vấn ở backend). Dùng sau khi đọc/xoá
     * thông báo thay vì gọi lại get-me (6 truy vấn) chỉ để lấy mảng này.
     */
    myNotifications: () => {
        const baseURL = "notification";
        return axiosClient.get(baseURL);
    },
    readAllNoti: () => {
        const baseURL = "notification/read";
        return axiosClient.put(baseURL);
    },
    deleteNotification: (id) => {
        const baseURL = `notification/${id}`;
        return axiosClient.delete(baseURL);
    },
    deleteAllNotifications: () => {
        const baseURL = "notification/all";
        return axiosClient.delete(baseURL);
    },
    totalUser() {
        const baseURL = "user/total_user";
        return axiosClient.get(baseURL);
    },
    topOfMonth(startTime = "", endTime = "") {
        const baseURL = `user/top_user_month?startTime=${startTime}&endTime=${endTime}`;
        return axiosClient.get(baseURL);
    },
    topOrdersOfMonth(startTime = "", endTime = "") {
        const baseURL = `user/top_user_orders?startTime=${startTime}&endTime=${endTime}`;
        return axiosClient.get(baseURL);
    },
    changeAvatar(formData) {
        const baseURL = "user/change_avatar";
        return axiosClient.post(baseURL, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },
    deleteAccount() {
        const baseURL = "user/delete-account";
        return axiosClient.delete(baseURL);
    },
};

export default authApi;
